//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * Session Storage State Composable
 *
 * Creates reactive state that automatically persists to sessionStorage.
 * Combines Nuxt useState with automatic sessionStorage synchronization.
 *
 * Responsibilities:
 * - Initialize state from sessionStorage if available
 * - Watch state changes and sync to sessionStorage
 * - Handle sessionStorage errors gracefully (quota, blocked, etc.)
 * - Cleanup watcher on component unmount
 * - Support deep watching for nested objects
 *
 * Persistence:
 * - Data survives page refreshes within same tab
 * - Data cleared when tab/browser closes
 * - Not shared across tabs (unlike localStorage)
 *
 * @param key - Storage key for sessionStorage
 * @param initialValue - Default value if nothing in storage
 * @returns Reactive state synced with sessionStorage
 */
export function useSessionState<T>(key: string, initialValue: T) {
  const state = useState<T>(`session-${key}`, () => {
    // Attempt to load from sessionStorage
    try {
      const raw = sessionStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : initialValue;
    } catch {
      return initialValue;
    }
  });

  // Write to sessionStorage whenever state changes
  const stop = watch(
    state,
    (value) => {
      try {
        sessionStorage.setItem(key, JSON.stringify(value));
      } catch (_) {
        // storage full or blocked — ignore
      }
    },
    { deep: true },
  );

  // Clear watcher when composable is used inside a component
  if (getCurrentInstance()) {
    onUnmounted(() => stop());
  }

  return state;
}
